import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import SearchBar from '@/features/documents/components/search-bar';
import {
  getTrackingIntervalDisplay,
  TrackingInterval,
} from '@/features/projects/create-subproject/types/tracking-interval';
import { ProjectStatusIndicator } from '@/features/projects/projects-hub/components/project-status-indicator';
import { useDeleteProjectMutation } from '@/features/projects/shared/hooks/use-delete-project-mutation';
import { getProjectSubprojectsQueryKey } from '@/features/projects/shared/hooks/use-project-subprojects';
import {
  getFilteredSubprojectsQueryKey,
  useProjectSubprojectsFiltered,
} from '@/features/projects/shared/hooks/use-project-subprojects-filtered';
import { ProjectStatus } from '@/features/projects/shared/types/project-status';
import { SubprojectHubFilter } from '@/features/projects/subprojects-hub/components/subproject-hub-filter';
import {
  useHasSubprojectHubFilters,
  useSubprojectHubFilterStore,
} from '@/features/projects/subprojects-hub/hooks/subprojects-filter';
import { PROJECT_ENTITLEMENTS_QUERY_KEY } from '@/features/subscriptions/hooks/use-project-entitlements';
import { PROJECT_PLAN_USAGE_COUNTS_QUERY_KEY } from '@/features/subscriptions/hooks/use-project-plan-usage-counts';
import { DefaultLayout } from '@/shared/components/layouts/default-layout';
import { LoadingIndicator } from '@/shared/components/loading-indicator';
import { EmptyTablePlaceholder } from '@/shared/components/table/empty-table-placeholder';
import { CustomTable } from '@/shared/components/table/table';
import { CustomTableHeader } from '@/shared/components/table/table-header';
import { CustomTableRow } from '@/shared/components/table/table-row';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { TableBody } from '@/shared/components/ui/table';
import { getInitials } from '@/shared/utils/avatar-util';

export const SubprojectHubPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('subproject', { keyPrefix: 'Hub' });
  const { t: sharedT } = useTranslation('project', { keyPrefix: 'Shared' });
  const { t: subProjectTableT } = useTranslation('project', {
    keyPrefix: 'SubProjectTable',
  });
  const { t: trackingIntervalT } = useTranslation('subproject', {
    keyPrefix: 'TrackingInterval',
  });

  if (!projectId) {
    navigate(paths.projects.hub.getHref());
    return null;
  }

  const { searchQuery, setSearchQuery } = useSubprojectHubFilterStore();
  const hasFilters = useHasSubprojectHubFilters();

  const {
    isLoading,
    data: subProjects,
    isError,
  } = useProjectSubprojectsFiltered({
    projectId: Number(projectId),
  });

  const queryClient = useQueryClient();

  const deleteMutation = useDeleteProjectMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getFilteredSubprojectsQueryKey({
          projectId: Number(projectId),
        }),
      });
      queryClient.invalidateQueries({
        queryKey: getProjectSubprojectsQueryKey({
          projectId: Number(projectId),
        }),
      });
      queryClient.invalidateQueries({
        queryKey: [PROJECT_PLAN_USAGE_COUNTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [PROJECT_ENTITLEMENTS_QUERY_KEY],
      });
    },
  });

  const handleDeleteSubproject = async (id: number) => {
    await deleteMutation.mutateAsync({ id });
  };

  const columns = [
    subProjectTableT('title'),
    subProjectTableT('status'),
    subProjectTableT('partner'),
    subProjectTableT('trackingInterval'),
    subProjectTableT('notifications'),
    subProjectTableT('lastEntry'),
  ];

  const columnWidths = [
    'w-[257px]',
    'w-[256px]',
    'w-[258px]',
    'w-[257px]',
    'w-[256px]',
    'flex-1',
  ];

  return (
    <DefaultLayout
      title={t('title')}
      headerActions={[
        <SearchBar
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('search_placeholder')}
        />,
        <SubprojectHubFilter />,
      ]}
    >
      {isLoading ? (
        <LoadingIndicator className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
      ) : isError || !subProjects ? (
        <div>{sharedT('could_not_fetch_subprojects')}</div>
      ) : subProjects.length === 0 ? (
        <EmptyTablePlaceholder
          columns={columns}
          title={
            hasFilters
              ? subProjectTableT('no_subprojects_found')
              : subProjectTableT('no_subprojects')
          }
          dottedBorder
        />
      ) : (
        <CustomTable>
          <CustomTableHeader columns={columns} columnWidths={columnWidths} />
          <TableBody>
            {subProjects.map((row, index) => {
              const contractor = row.contractors[0];
              const isInternal =
                contractor &&
                contractor.owner_orga_id != null &&
                contractor.partner_orga_id != null &&
                contractor.owner_orga_id === contractor.partner_orga_id;
              const partnerName = isInternal
                ? subProjectTableT('internal_partner')
                : contractor?.alias;

              return (
                <CustomTableRow
                  key={row.id}
                  props={[
                    {
                      value: { type: 'string', text: row.name ?? '' },
                      isLeftMost: true,
                      width: columnWidths[0] || 'flex-1',
                    },
                    {
                      value: {
                        type: 'custom',
                        item: (
                          <ProjectStatusIndicator
                            status={row.status as ProjectStatus}
                          />
                        ),
                      },
                      width: columnWidths[1] || 'flex-1',
                    },
                    {
                      value: {
                        type: 'custom',
                        item: partnerName ? (
                          <div className="flex items-center gap-2">
                            <Avatar size="sm">
                              <AvatarFallback>
                                {getInitials(partnerName)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-2xs font-normal">
                              {partnerName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-2xs text-muted-foreground">
                            —
                          </span>
                        ),
                      },
                      width: columnWidths[2] || 'flex-1',
                    },
                    {
                      value: {
                        type: 'string',
                        text: row.tracking_interval
                          ? getTrackingIntervalDisplay({
                              interval:
                                row.tracking_interval as TrackingInterval,
                              t: trackingIntervalT,
                            })
                          : '—',
                      },
                      width: columnWidths[3] || 'flex-1',
                    },
                    row.unread_notification_count > 0
                      ? {
                          value: {
                            type: 'number',
                            number: row.unread_notification_count,
                          },
                          width: columnWidths[4] || 'flex-1',
                        }
                      : {
                          value: {
                            type: 'string',
                            text: subProjectTableT('no_notifications'),
                          },
                          width: columnWidths[4] || 'flex-1',
                        },
                    row.last_entry_date
                      ? {
                          value: {
                            type: 'date',
                            date: new Date(row.last_entry_date),
                          },
                          isRightMost: true,
                          width: columnWidths[5] || 'flex-1',
                        }
                      : {
                          value: {
                            type: 'string',
                            text: subProjectTableT('no_entries'),
                          },
                          isRightMost: true,
                          width: columnWidths[5] || 'flex-1',
                        },
                  ]}
                  type="single"
                  isFirstRow={index === 0}
                  isLastRow={index === subProjects.length - 1}
                  onClick={() =>
                    navigate(
                      paths.projects.details.base.getHref(row.id.toString()),
                    )
                  }
                  actions={[
                    {
                      action: <p>{t('actions.delete')}</p>,
                      onClick: () => handleDeleteSubproject(row.id),
                    },
                  ]}
                />
              );
            })}
          </TableBody>
        </CustomTable>
      )}
    </DefaultLayout>
  );
};
